import { Injectable } from '@angular/core';
import * as PubNub from 'pubnub';
import { environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NavigationExtras, Router } from '@angular/router';
import { CommanSubscribeService } from './comman-subscribe.service';
import { AppService } from './app.service';
import { API } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class GlobalPubnubService {
  pubnub: any;
  private notifications$ = new BehaviorSubject<any[]>([]);
  // Remembered so a later dynamic-keyset rebuild in init() can resubscribe
  // this fan's notification channel on the new client - subscribeToChannels()
  // is normally called once right after init() and nothing else would know
  // to redo it once the org's real keyset replaces the static placeholder.
  private currentUserId: any = null;
  constructor(private toastrService: ToastrService,
    private router: Router,
    private commanSubscribeService: CommanSubscribeService,
    private appService: AppService
  ) { }

  init () {
    this.pubnub = new PubNub({
        publishKey: environment.pubNub.publishKey,
        subscribeKey: environment.pubNub.subscribeKey,
        uuid: 'sample-uuid'
    });
    this.attachListener();

    // Static keys above work as-is for orgs still on the shared/legacy
    // keyset. Orgs migrated to their own dedicated PubNub keyset publish
    // fan_{userId}.user-notification under THAT keyset instead - without
    // this, this client would stay subscribed on the wrong keyset forever
    // and those fans would never receive notifications sent post-migration.
    if (environment.DYNAMIC_PUBNUB_ENABLED) {
      this.commanSubscribeService.getPubnubkeysetInfo().subscribe((response) => {
        let parsedResponse: any;
        try {
          parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;
        } catch (error) {
          console.error('Failed to parse PubNub keyset response:', error);
          parsedResponse = null;
        }
        const publishKey = parsedResponse?.pubnub_publish_key;
        const subscribeKey = parsedResponse?.pubnub_subscribe_key;
        if (!publishKey || !subscribeKey) return; // still waiting on the real org keyset

        this.pubnub.unsubscribeAll();
        this.pubnub = new PubNub({ publishKey, subscribeKey, uuid: 'sample-uuid' });
        this.attachListener();
        // A dedicated org keyset almost always has Access Manager enabled,
        // unlike the shared/legacy one - every request on it needs a valid
        // token or PubNub 403s (see pubnub.service.ts's connect()). Reuse
        // whatever token that service already fetched for this fan rather
        // than requesting a second one - the token is scoped per-fan, not
        // per PubNub client instance.
        const cachedToken = localStorage.getItem('pubnub_token');
        if (cachedToken) {
          this.pubnub.setToken(cachedToken);
        }
        if (this.currentUserId) {
          this.subscribeToChannels(this.currentUserId);
        }
      });
    }
  }

  private attachListener() {
    this.pubnub.addListener({
      message: (event) => {
        const msg = event.message;
        console.log("global-addLIster",msg)
        this.handleIncomingMessage(msg);
      },
      status: (statusEvent) => {
        console.log('PubNub status event:', statusEvent);
      }
    });
  }

  private handleIncomingMessage(msg: any) {
    console.log("handleIncomingMessage => RAW:", msg, typeof msg);

    // Parse if message is string
    if (typeof msg === 'string') {
      try {
        msg = JSON.parse(msg);
      } catch (e) {
        console.error("Invalid JSON string message:", msg);
        return;
      }
    }

    const inApp = msg?.in_app;
    if (!inApp) {
      console.warn("No in_app payload found");
      return;
    }
    const text = this.escapeHtml(inApp.text || "New notification");
    const meta = inApp.metaData || {};
    const type = meta.type || "";
    const isHard = meta.notificationType === "hard";
    // Toast options
    const toastrOptions: any = {
      positionClass: 'toast-top-right',
      toastClass: 'ngx-toastr pubnub-toast',
      timeOut: isHard ? 0 : 8000,
      extendedTimeOut: isHard ? 0 : 1000,
      closeButton: isHard,
      progressBar: !isHard
    };

    let finalMessage = text;
    if (isHard) {
      finalMessage += ` <span class="toast-link" style="cursor:pointer;text-decoration:underline;">Click here</span>`;
    }
    let toastTitle = '';

    switch (type) {
      case 'event-reminder':
        toastTitle = 'Event Reminder';
        break;
      case 'event-offer':
        toastTitle = 'Event Offer';
        break;
      case 'merchandise-offer':
        toastTitle = 'Merchandise Offer';
        break;
      case 'merchandise-reminder':
        toastTitle = 'Merchandise Reminder';
        break;
    }
    // Optional: clear existing toasts to prevent stacking
    this.toastrService.clear();
    this.toastrService.info(finalMessage, toastTitle, toastrOptions);
    // Only clickable for hard notifications
    if (!isHard) return;
    setTimeout(() => {
      const toastEl = document.querySelector('.ngx-toastr.pubnub-toast:last-child');
      if (!toastEl) return;
      const linkEl: any = toastEl.querySelector('.toast-link');
      if (!linkEl) return;
      linkEl.addEventListener('click', () => {
        console.log("Clicked Notification Type:", type);
        // Fire-and-forget communication tracking — must never delay or
        // block navigation, and a failure here must never affect it.
        this.trackNotificationClick(meta);
        // Navigate immediately; do not wait on the toast's own close
        // animation/removal chain (manualClose -> easeTime timeout -> close),
        // which does not reliably emit onHidden after clear().
        this.navigateFromNotification(type, meta);
        this.toastrService.clear();
      });
    }, 150);
  }

private openInNewTab(commands: any[], extras?: NavigationExtras) {
  const url = this.router.serializeUrl(this.router.createUrlTree(commands, extras));
  window.open(url, '_blank');
}

  // Common click-tracking call for all six push notification types (Churn
  // Recommendation/Offer, Merch Reminder/Offer, Event Recommendation/Offer)
  // — communication_id/org_id come from the same in_app.metaData already
  // used for navigation above, so no per-type branching is needed here.
  private trackNotificationClick(meta: any) {
    if (!meta?.communication_id) {
      // Logged (not thrown) so a missing field on the notification payload
      // is visible in DevTools instead of silently doing nothing.
      console.warn('trackNotificationClick: skipped — meta.communication_id missing', meta);
      return;
    }
    try {
      const payload = {
        communication_id: meta.communication_id,
        user_id: this.commanSubscribeService.userInfo?.id,
        org_id: meta.org_id,
        event_type: 'clicked',
        client_type: 'web',
        client_timestamp: new Date().toISOString(),
      };
      console.log('trackNotificationClick: sending', payload);
      const req = this.appService.postBaseRequest(API.COMMUNICATION_EVENTS, payload);
      // postBaseRequest returns undefined when offline (it shows its own
      // toast in that case) — guard so tracking can never throw here.
      if (!req) {
        console.warn('trackNotificationClick: postBaseRequest returned nothing (offline?)');
        return;
      }
      req.subscribe({
        next: (res) => console.log('trackNotificationClick: recorded', res),
        // Logged, never rethrown/blocking — a tracking failure (e.g. unknown
        // communication_id, org_id/user_id mismatch) must never affect
        // navigation, only be visible for debugging.
        error: (err) => console.warn('trackNotificationClick: failed', err),
      });
    } catch (e) {
      // Never let tracking break the click flow.
      console.warn('trackNotificationClick: unexpected error', e);
    }
  }

private navigateFromNotification(type: string, meta: any) {
  const eventSlug = meta.eventSlug || "";
  const cartId = meta.cartId || "";
  const code = meta.code || "";
  const networkName = meta.networkName || "";
  const productId = meta.productId || "";
  const collectionId = meta.collectionIds || "";
  const channelName = networkName ? networkName : this.router.url.split('/')[1];

  if (type === 'event-recommendation' && eventSlug && networkName) {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab([`${base}/event/${eventSlug}`]);
  }
  else if (type === 'product-recommendation') {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab([`${base}/merchandise/overviewProduct/${productId}/${collectionId}`]);
  }
  else if (type === 'content-recommendation') {
    const base = this.commanSubscribeService.getChannelBasePath(meta.channelName);
    this.openInNewTab([`${base}/album/${meta.vodVideoAudio}/${meta.contentSlug}`]);
  }
  else if (type === 'space-recommendation') {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab([`${base}/community/${meta.spaceId}`]);
  }
  else if (type === 'event-offer' && eventSlug) {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab(
      [`${base}/event/${eventSlug}/place-order`],
      { queryParams: { code } }
    );
  }
  else if (type === 'event-reminder' && eventSlug) {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab([`${base}/event/${eventSlug}/place-order`]);
  }
  else if (type === 'merchandise-offer' && cartId) {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab(
      [`${base}/merchandise/product-shipping/${cartId}`],
      { queryParams: { code } }
    );
  }
  else if (type === 'merchandise-reminder' && cartId) {
    const base = this.commanSubscribeService.getChannelBasePath(channelName);
    this.openInNewTab([`${base}/merchandise/product-shipping/${cartId}`]);
  }
  else {
    this.openInNewTab([this.commanSubscribeService.getChannelBasePath(channelName)]);
  }
}
  

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  getNotifications() {
    return this.notifications$.asObservable();
  }

  subscribeToChannels(userId:any) {
    this.currentUserId = userId;
    // Must match the token's authorized_uuid (generateToken() mints it as
    // 'fan_' + id) - PAM rejects a request whose connection uuid doesn't
    // match the token it presents, regardless of what the token grants.
    this.pubnub.setUUID('fan_' + userId);
    const channel = `fan_${userId}.user-notification`;
    this.pubnub.subscribe({
      channels: [channel]
    });
  };
}
